// Dropdown panel anchored under the bell icon in Header. Lists the recent
// notifications, lets the user clear them individually (the 'x' button on
// each row) or all at once (Clear all in the panel header). Clicking the
// row body deep-links to the source module and marks that row read in one
// go.
//
// Read/unread styling mirrors the SMRT Docs green-traffic pattern:
//   • Unread = green left accent + bold message
//   • Read   = muted, no accent (the "completed" look)
// Background uses the SMRT Access dark-slate palette so the panel reads
// as a peer of those module panels.
//
// Routing: the `link` stored on each notification is a path + query string
// produced by notificationService (e.g. "/smrt-tasks?taskId=abc"). The
// frontend modules already parse their own query params; we just navigate
// to the link verbatim.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellNotification } from "../hooks/useNotifications";
import "./NotificationPanel.css";

interface Props {
  notifications: BellNotification[];
  onClose: () => void;
  onMarkRead: (id: string) => void | Promise<void>;
  onDismiss: (id: string) => void | Promise<void>;
  onClearAll: () => void | Promise<void>;
}

// Possible UI states for the panel. List is the default view; confirmClear
// replaces the list with a confirmation prompt when the user clicks Clear
// all so the destructive action isn't a single accidental tap.
type PanelView = "list" | "confirmClear";

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Module label rendered as a small chip beside each notification so the
// user can scan by type at a glance.
function typeLabel(t: BellNotification["type"]): string {
  if (t === "task") return "Tasks";
  if (t === "invite") return "Invites";
  if (t === "doc") return "Docs";
  return t;
}

export default function NotificationPanel({
  notifications,
  onClose,
  onMarkRead,
  onDismiss,
  onClearAll,
}: Props) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<PanelView>("list");

  // Click-outside-to-close. Bound on the document so taps anywhere outside
  // the panel collapse it. The bell button itself intercepts its own click
  // (toggling) — guarding here against the panel-internal clicks bubbling
  // up and self-closing.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Defer one tick so the same click that opened the panel doesn't
    // immediately close it.
    const t = setTimeout(
      () => document.addEventListener("mousedown", onDocClick),
      0,
    );
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [onClose]);

  const handleRowClick = (n: BellNotification) => {
    if (!n.read) onMarkRead(n.id);
    onClose();
    // Invite notifications ("an invited user has joined") are informational —
    // just mark read + close. They have no useful in-app destination for every
    // recipient (e.g. a collaborator owner without users.manage), and older
    // ones carry a stale link; navigating either case bounced users to the
    // login / org-picker screen.
    if (n.link && n.type !== "invite") navigate(n.link);
  };

  const handleConfirmClear = async () => {
    await onClearAll();
    // After a successful Clear-all the panel would otherwise sit on the
    // "No notifications yet" empty state — close it so the user isn't
    // left with an empty dropdown still hanging on the screen.
    setView("list");
    onClose();
  };

  return (
    <div
      className="bell-panel"
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
    >
      <div className="bell-panel-head">
        <span className="bell-panel-title">Notifications</span>
        {view === "list" && notifications.length > 0 && (
          <button
            type="button"
            className="bell-panel-clear-all"
            onClick={() => setView("confirmClear")}
          >
            Clear all
          </button>
        )}
      </div>

      {view === "confirmClear" ? (
        <div className="bell-panel-confirm">
          <p className="bell-panel-confirm-message">
            Clear all {notifications.length} notification
            {notifications.length === 1 ? "" : "s"}? This can&apos;t be
            undone.
          </p>
          <div className="bell-panel-confirm-actions">
            <button
              type="button"
              className="bell-panel-btn bell-panel-btn-ghost"
              onClick={() => setView("list")}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bell-panel-btn bell-panel-btn-danger"
              onClick={handleConfirmClear}
              autoFocus
            >
              Clear all
            </button>
          </div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="bell-panel-empty">No notifications yet.</div>
      ) : (
        <ul className="bell-panel-list">
          {notifications.map((n) => (
            <li
              key={n.id}
              className={`bell-panel-row${n.read ? " bell-panel-row-read" : " bell-panel-row-unread"}`}
            >
              <button
                type="button"
                className="bell-panel-row-button"
                onClick={() => handleRowClick(n)}
                aria-label={`${n.message}. ${formatRelative(n.createdAt)}. ${n.read ? "Read" : "Unread"}.`}
              >
                <span className="bell-panel-row-message">{n.message}</span>
                <span className="bell-panel-row-meta">
                  <span className="bell-panel-row-chip">{typeLabel(n.type)}</span>
                  <span className="bell-panel-row-time">
                    {formatRelative(n.createdAt)}
                  </span>
                </span>
              </button>
              <button
                type="button"
                className="bell-panel-row-dismiss"
                onClick={(e) => {
                  // Don't trigger the row-button click handler beneath us.
                  e.stopPropagation();
                  onDismiss(n.id);
                }}
                aria-label="Dismiss notification"
                title="Dismiss"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
